import React, { useState, useEffect, useRef, useCallback } from 'react';
import { debounce } from 'lodash';
import AdminSidebar from "../components/AdminSidebar";
import AdminNavbar from '../components/AdminNavbar';
import { useSidebar } from '../components/SidebarContext';
import api from "../services/api";
import SortIcon from '@mui/icons-material/Sort';

const AllPayments = () => {
  const { isSidebarOpen } = useSidebar();
  const sidebarWidth = isSidebarOpen ? '250px' : '70px';

  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const paymentsPerPage = 100;
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [isPageDropdownOpen, setIsPageDropdownOpen] = useState(false); // New state for custom page dropdown

  const tableContainerRef = useRef(null);

  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTo(0, 0);
    }
  }, [currentPage]);

  const fetchAllPayments = useCallback(async (skip = 0, limit = paymentsPerPage, sortBy = 'createdAt', sortOrder = 'desc') => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: skip.toString(),
        sortBy,
        sortOrder,
      });

      console.log(`Fetching payments with params: ${params.toString()}`);
      const response = await api.get(`/api/payments/all-payments?${params}`);
      console.log('API Response:', response.data);
      return response.data.payments || [];
    } catch (err) {
      console.error('Error fetching payments:', err, err.response?.data);
      setError(err.response?.data?.message || "Failed to fetch payment data.");
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTotalRecords = useCallback(async () => {
    try {
      console.log('Fetching total records');
      const response = await api.get(`/api/payments/all-payments/count`);
      console.log('Total Records Response:', response.data);
      return response.data.total || 0;
    } catch (err) {
      console.error('Error fetching total records:', err, err.response?.data);
      setError(err.response?.data?.message || "Failed to fetch total records.");
      return 0;
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setError(null);
      setLoading(true);

      try {
        const total = await fetchTotalRecords();
        setTotalRecords(total);

        let allPayments = [];
        const limit = paymentsPerPage;
        let skip = 0;

        while (skip < total) {
          const batch = await fetchAllPayments(skip, limit, 'createdAt', 'desc');
          if (batch.length === 0) break;
          allPayments = [...allPayments, ...batch];
          skip += limit;
        }

        // Apply status filtering and sorting on frontend
        let filteredPayments = allPayments;
        if (statusFilter !== 'all') {
          filteredPayments = allPayments.filter(payment => {
            const statusDisplay = getStatusColor(payment.status).display.toLowerCase();
            return statusFilter === statusDisplay.toLowerCase();
          });
        }

        // Sort by status if statusFilter is not 'all'
        if (statusFilter !== 'all') {
          filteredPayments.sort((a, b) => {
            const statusA = getStatusColor(a.status).display.toLowerCase();
            const statusB = getStatusColor(b.status).display.toLowerCase();
            return statusA === 'success' && statusB === 'failed' ? -1 : statusA === 'failed' && statusB === 'success' ? 1 : 0;
          });
        }

        setPayments(allPayments);
        setFilteredPayments(filteredPayments);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load payment data.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [statusFilter, fetchTotalRecords, fetchAllPayments]);

  const debouncedSearch = useCallback(
    debounce((query) => {
      setSearchQuery(query);
    }, 300),
    []
  );

  useEffect(() => {
    const filtered = payments.filter(payment => {
      const studentName = (payment.studentName || '').toLowerCase();
      const studentEmail = (payment.studentEmail || '').toLowerCase();
      const departmentName = (payment.departmentName || '').toLowerCase();
      const testName = (payment.testName || '').toLowerCase();
      const method = (payment.method || '').toLowerCase();
      const transactionId = (payment.transactionId || '').toLowerCase();
      const amount = payment.amount ? (payment.amount / 100).toFixed(2).toLowerCase() : '';
      const date = payment.date
        ? new Date(payment.date).toLocaleDateString("en-GB", {
            year: "numeric",
            month: "short",
            day: "numeric",
          }).toLowerCase()
        : '';
      const search = searchQuery.toLowerCase();

      const matchesSearch = (
        studentName.includes(search) ||
        studentEmail.includes(search) ||
        departmentName.includes(search) ||
        testName.includes(search) ||
        method.includes(search) ||
        transactionId.includes(search) ||
        amount.includes(search) ||
        date.includes(search)
      );

      // Apply status filter
      if (statusFilter === 'all') {
        return matchesSearch;
      }
      const statusDisplay = getStatusColor(payment.status).display.toLowerCase();
      return matchesSearch && statusFilter === statusDisplay.toLowerCase();
    });

    setFilteredPayments(filtered);
    setCurrentPage(1);
  }, [searchQuery, statusFilter, payments]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-sort-dropdown]') && !event.target.closest('[data-page-dropdown]')) {
        setShowSortDropdown(false);
        setIsPageDropdownOpen(false);
      }
    };

    if (showSortDropdown || isPageDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showSortDropdown, isPageDropdownOpen]);

  const totalPages = Math.ceil(filteredPayments.length / paymentsPerPage);
  const indexOfLastPayment = currentPage * paymentsPerPage;
  const indexOfFirstPayment = indexOfLastPayment - paymentsPerPage;
  const currentPayments = filteredPayments.slice(indexOfFirstPayment, indexOfLastPayment);

  const paginate = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
    setIsPageDropdownOpen(false); // Close dropdown after selection
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'success':
      case 'used':
        return { display: 'Success', color: '#22c55e' };
      case 'failed':
      case 'created':
        return { display: 'Failed', color: '#ef4444' };
      default:
        return { display: status || 'Unknown', color: '#6b7280' };
    }
  };

  const formatCurrency = (amount, currency = 'INR') => {
    const convertedAmount = amount / 100;
    return currency === 'INR' ? `₹${convertedAmount.toFixed(2)}` : `${currency} ${convertedAmount.toFixed(2)}`;
  };

  const SortDropdown = () => (
    <div style={{ position: 'relative', display: 'inline-block', fontFamily: "'Roboto', sans-serif" }} data-sort-dropdown>
      <SortIcon
        onClick={() => setShowSortDropdown(!showSortDropdown)}
        style={{
          fontSize: '24px',
          color: '#6B21A8',
          cursor: 'pointer',
          transition: 'transform 0.3s ease',
          fontFamily: "'Roboto', sans-serif",
        }}
        className="sort-icon-btn"
        title={`Filter: ${statusFilter === 'all' ? 'All Status' : getStatusColor(statusFilter).display}`}
      />
      {showSortDropdown && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: '0',
            backgroundColor: '#fff',
            border: '1px solid #ddd',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            zIndex: 1003,
            minWidth: '120px',
            marginTop: '4px',
            fontFamily: "'Roboto', sans-serif",
          }}
        >
          {['all', 'success', 'failed'].map((status) => (
            <button
              key={status}
              onClick={() => {
                setStatusFilter(status);
                setShowSortDropdown(false);
                setCurrentPage(1);
              }}
              style={{
                display: 'block',
                width: '100%',
                padding: '8px 12px',
                background: statusFilter === status ? '#f0f0f0' : 'transparent',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '12px',
                color: '#333',
                fontFamily: "'Roboto', sans-serif",
              }}
              onMouseEnter={(e) => (e.target.style.background = '#f0f0f0')}
              onMouseLeave={(e) => (e.target.style.background = statusFilter === status ? '#f0f0f0' : 'transparent')}
            >
              {status === 'all' ? 'All Status' : status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const styles = {
    container: {
      display: 'flex',
      height: '100vh',
      overflow: 'hidden',
      minHeight: '100vh',
      background: 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)',
      fontFamily: "'Roboto', sans-serif",
    },
    sidebar: {
      width: sidebarWidth,
      borderRadius: '30px',
      height: '100vh',
      flexShrink: 0,
      position: 'fixed',
      top: 0,
      left: 0,
      zIndex: 10,
      transition: 'width 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    content: {
      flex: 1,
      padding: '15px',
      margin: '6rem 2rem 2rem 2rem',
      backgroundColor: 'white',
      transition: 'margin-left 0.3s ease',
      height: 'calc(100vh - 8rem)',
      borderTopLeftRadius: '1rem',
      borderTopRightRadius: '1rem',
      borderBottomLeftRadius: '1rem',
      borderBottomRightRadius: '1rem',
      maxWidth: `calc(100vw - ${sidebarWidth} - 4rem)`,
      width: '100%',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      fontFamily: "'Roboto', sans-serif",
    },
    headerContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
      alignItems: 'center',
      marginBottom: '16px',
      width: '100%',
      flexShrink: 0,
      marginRight: '16px',
      fontFamily: "'Roboto', sans-serif",
    },
    searchFilterGroup: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '8px',
      fontFamily: "'Roboto', sans-serif",
    },
    searchInput: {
      width: '80%',
      maxWidth: '450px',
      padding: '6px 12px',
      borderRadius: '30px',
      border: '1px solid #ddd',
      fontSize: '14px',
      outline: 'none',
      transition: 'border-color 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    tableContainer: {
      flex: 1,
      overflowY: 'auto',
      overflowX: 'auto',
      borderRadius: '0.5rem',
      maxHeight: 'calc(100vh - 12rem)',
      width: '100%',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: "'Roboto', sans-serif",
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      border: 'none',
      fontSize: '12px',
      fontFamily: "'Roboto', sans-serif",
    },
    headerRow: {
      background: 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)',
      position: 'sticky',
      top: 0,
      zIndex: 2,
      fontFamily: "'Roboto', sans-serif",
    },
    th: {
      padding: '12px 10px',
      fontWeight: '500',
      textAlign: 'center',
      color: '#6B21A8',
      fontSize: '12px',
      borderBottom: '2px solid #e5e7eb',
      fontFamily: "'Roboto', sans-serif",
    },
    thStudent: {
      padding: '12px 10px',
      fontWeight: '500',
      textAlign: 'left',
      color: '#6B21A8',
      fontSize: '12px',
      borderBottom: '2px solid #e5e7eb',
      fontFamily: "'Roboto', sans-serif",
    },
    thTransactionId: {
      padding: '12px 10px',
      fontWeight: '500',
      textAlign: 'center',
      color: '#6B21A8',
      fontSize: '12px',
      borderBottom: '2px solid #e5e7eb',
      fontFamily: "'Roboto', sans-serif",
    },
    row: {
      borderRadius: '8px',
      fontFamily: "'Roboto', sans-serif",
    },
    td: {
      padding: '10px 12px',
      verticalAlign: 'middle',
      textAlign: 'center',
      color: '#333',
      fontFamily: "'Roboto', sans-serif",
    },
    tdStudent: {
      padding: '10px 12px',
      verticalAlign: 'middle',
      textAlign: 'left',
      color: '#333',
      fontFamily: "'Roboto', sans-serif",
    },
    tdTransactionId: {
      padding: '12px 12px',
      verticalAlign: 'middle',
      textAlign: 'center',
      color: '#333',
      fontFamily: "'Roboto', sans-serif",
    },
    statusBadge: {
      padding: '4px 8px',
      borderRadius: '12px',
      fontSize: '10px',
      fontWeight: '500',
      color: 'white',
      textTransform: 'capitalize',
      fontFamily: "'Roboto', sans-serif",
    },
    loadingContainer: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '16px',
      color: '#666',
      fontFamily: "'Roboto', sans-serif",
    },
    errorContainer: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '16px',
      color: 'red',
      fontFamily: "'Roboto', sans-serif",
    },
    noDataContainer: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '16px',
      color: '#666',
      fontFamily: "'Roboto', sans-serif",
    },
    paginationContainer: {
      background: '#ffffff',
      padding: '10px 0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      minHeight: '40px',
      marginTop: '20px',
      zIndex: 5,
      fontFamily: "'Roboto', sans-serif",
    },
    pageButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '3px 13px',
      fontSize: '13px',
      fontWeight: '600',
      color: '#6B21A8',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      cursor: 'pointer',
      borderRadius: '30px',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      fontFamily: "'Roboto', sans-serif",
      flexShrink: 0,
      minWidth: '80px',
      textAlign: 'center',
      transition: 'transform 0.2s ease, background 0.3s ease, color 0.3s ease',
    },
    pageButtonPrevious: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '3px 13px',
      fontSize: '13px',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      cursor: 'pointer',
      borderRadius: '30px',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      fontFamily: "'Roboto', sans-serif",
      flexShrink: 0,
      minWidth: '80px',
      textAlign: 'center',
      transition: 'transform 0.2s ease, background 0.3s ease, color 0.3s ease',
      color: currentPage === 1 ? '#aaa' : '#6B21A8',
    },
    pageNumberButton: {
      width: '30px',
      height: '30px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '13px',
      fontWeight: '600',
      color: '#333',
      cursor: 'pointer',
      borderRadius: '50%',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      fontFamily: "'Roboto', sans-serif",
      transition: 'transform 0.2s ease',
    },
    paginationWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontFamily: "'Roboto', sans-serif",
    },
    customDropdown: {
      width: '60px',
      padding: '5px 25px 5px 10px',
      borderRadius: '5px',
      border: '1px solid #ccc',
      fontSize: '14px',
      color: '#1F2937',
      cursor: 'pointer',
      backgroundColor: '#fff',
      appearance: 'none',
      WebkitAppearance: 'none',
      MozAppearance: 'none',
      backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%236B21A8%22%20d%3D%22M10.293%203.293L6%207.586%201.707%203.293A1%201%200%2000%20.293%204.707l5%205a1%201%200%20001.414%200l5-5a1%201%200%2010-1.414-1.414z%22%2F%3E%3C%2Fsvg%3E")',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 10px center',
      backgroundSize: '12px',
      transition: 'border-color 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
      position: 'relative',
    },
    customDropdownOptions: {
      position: 'absolute',
      bottom: '100%',
      left: 0,
      width: '60px',
      border: '1px solid #ccc',
      borderRadius: '5px',
      backgroundColor: '#fff',
      zIndex: 1000,
      maxHeight: '200px',
      overflowY: 'auto',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      fontFamily: "'Roboto', sans-serif",
    },
    customOption: {
      padding: '5px 10px',
      cursor: 'pointer',
      fontFamily: "'Roboto', sans-serif",
    },
  };

  return (
    <div style={styles.container}>
      <style>{`
        input:focus {
          border-color: #6B21A8 !important;
          font-family: 'Roboto', sans-serif !important;
        }
        .sort-icon-btn:hover {
          transform: scale(1.1);
          font-family: 'Roboto', sans-serif !important;
        }
        .custom-table {
          border: none !important;
          font-family: 'Roboto', sans-serif !important;
        }
        .custom-table th,
        .custom-table td {
          border: none !important;
          font-family: 'Roboto', sans-serif !important;
        }
        .custom-table tbody tr:nth-child(odd) {
          background-color: #FFFFFF;
        }
        .custom-table tbody tr:nth-child(even) {
          background: linear-gradient(to right, #FAFCFF, #FBF6FF, #FFF8FB);
        }
        .custom-table tbody tr:hover {
          background: linear-gradient(to right, #F0F5FF, #F5EDFF, #FFF0F5) !important;
        }
        .table-container::-webkit-scrollbar {
          width: 12px;
          height: 12px;
        }
        .table-container::-webkit-scrollbar-track {
          background: #f8f9fa;
          border-radius: 6px;
        }
        .table-container::-webkit-scrollbar-thumb {
          background: #6c757d;
          border-radius: 6px;
          border: 2px solid #f8f9fa;
        }
        .table-container::-webkit-scrollbar-thumb:hover {
          background: #495057;
        }
        .table-container::-webkit-scrollbar-corner {
          background: #f8f9fa;
        }
        .table-container {
          scrollbar-width: thin;
          scrollbar-color: #6c757d #f8f9fa;
          font-family: 'Roboto', sans-serif !important;
          position: relative;
          overflow-y: auto;
          overflow-x: hidden !important;
          display: flex;
          flex-direction: column;
        }
        @keyframes glow {
          0% {
            box-shadow: 0 0 5px rgba(107, 33, 168, 0.5);
          }
          50% {
            box-shadow: 0 0 15px rgba(107, 33, 168, 0.8);
          }
          100% {
            box-shadow: 0 0 5px rgba(107, 33, 168, 0.5);
          }
        }
        .pagination-button:hover:not(:disabled) {
          background: linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF);
          transform: scale(1.05);
          animation: glow 1.5s ease-in-out infinite;
        }
        .pagination-button:active:not(:disabled) {
          transform: scale(0.95);
        }
        .pagination-button:disabled {
          background-color: #ccc !important;
          color: #aaa !important;
          cursor: not-allowed;
        }
        .custom-dropdown {
          position: relative;
          width: 60px;
          padding: 5px 25px 5px 10px;
          border: 1px solid #ccc;
          border-radius: 5px;
          background-color: #fff;
          color: #1F2937;
          cursor: 'pointer';
          font-family: 'Roboto', sans-serif !important;
          backgroundImage: url("data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%236B21A8%22%20d%3D%22M10.293%203.293L6%207.586%201.707%203.293A1%201%200%2000%20.293%204.707l5%205a1%201%200%20001.414%200l5-5a1%201%200%2010-1.414-1.414z%22%2F%3E%3C%2Fsvg%3E");
          backgroundRepeat: no-repeat;
          backgroundPosition: right 10px center;
          backgroundSize: 12px;
        }

        .custom-dropdown-options {
          position: absolute;
          bottom: 100%;
          left: 0;
          width: 60px;
          border: 1px solid #ccc;
          border-radius: 5px;
          background-color: #fff;
          z-index: 1000;
          max-height: 200px;
          overflow-y: auto;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .custom-option {
          padding: 5px 10px;
          cursor: pointer;
        }

        .custom-option:hover {
          background: #D3D3D3 !important;
          color: #1F2937 !important;
        }

        .custom-dropdown-options::-webkit-scrollbar {
          width: 8px;
        }
        .custom-dropdown-options::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        .custom-dropdown-options::-webkit-scrollbar-thumb {
          background: #8f8f8f;
          border-radius: 4px;
        }
        .custom-dropdown-options::-webkit-scrollbar-thumb:hover {
          background: #6B21A8;
        }

        .search-input:hover {
          border-color: #6B21A8;
        }
      `}</style>

      <div style={styles.sidebar}>
        <AdminSidebar />
      </div>

      <div style={{ marginLeft: sidebarWidth, width: `calc(100% - ${sidebarWidth})`, transition: 'margin-left 0.3s ease', fontFamily: "'Roboto', sans-serif" }}>
        <AdminNavbar />
        <div style={styles.content}>
          <div style={styles.headerContainer}>
            <div style={styles.searchFilterGroup}>
              <input
                type="text"
                placeholder="Search by student, email, department, date, test, amount, method, or transaction..."
                onChange={(e) => debouncedSearch(e.target.value)}
                style={styles.searchInput}
                className="search-input"
                aria-label="Search payments"
              />
              <SortDropdown />
            </div>
          </div>

          {loading ? (
            <div style={styles.loadingContainer}>
              <p>Loading payment history...</p>
            </div>
          ) : error ? (
            <div style={styles.errorContainer}>
              <p>{error}</p>
              <button
                onClick={() => {
                  setError(null);
                  setCurrentPage(1);
                  setPayments([]);
                  setFilteredPayments([]);
                  fetchTotalRecords().then(total => {
                    setTotalRecords(total);
                    let allPayments = [];
                    let skip = 0;
                    const limit = paymentsPerPage;
                    const fetchAll = async () => {
                      while (skip < total) {
                        const batch = await fetchAllPayments(skip, limit, 'createdAt', 'desc');
                        if (batch.length === 0) break;
                        allPayments = [...allPayments, ...batch];
                        skip += limit;
                      }
                      let filteredPayments = allPayments;
                      if (statusFilter !== 'all') {
                        filteredPayments = allPayments.filter(payment => {
                          const statusDisplay = getStatusColor(payment.status).display.toLowerCase();
                          return statusFilter === statusDisplay.toLowerCase();
                        });
                        filteredPayments.sort((a, b) => {
                          const statusA = getStatusColor(a.status).display.toLowerCase();
                          const statusB = getStatusColor(b.status).display.toLowerCase();
                          return statusA === 'success' && statusB === 'failed' ? -1 : statusA === 'failed' && statusB === 'success' ? 1 : 0;
                        });
                      }
                      setPayments(allPayments);
                      setFilteredPayments(filteredPayments);
                    };
                    fetchAll();
                  });
                }}
                style={{
                  ...styles.pageButton,
                  marginTop: '10px',
                  cursor: 'pointer',
                }}
              >
                Retry
              </button>
            </div>
          ) : filteredPayments.length === 0 ? (
            <div style={styles.noDataContainer}>
              <p>No payment records found. Try adjusting the search or status filter.</p>
            </div>
          ) : (
            <div style={styles.tableContainer} className="table-container" ref={tableContainerRef}>
              <table className="custom-table" style={styles.table}>
                <thead style={styles.headerRow}>
                  <tr>
                    <th style={styles.th}>Sr.</th>
                    <th style={styles.thStudent}>Student</th>
                    <th style={styles.th}>Department</th>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Test Name</th>
                    <th style={styles.th}>Amount</th>
                    <th style={styles.th}>Payment Method</th>
                    <th style={styles.thTransactionId}>Transaction ID</th>
                    <th style={styles.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {currentPayments.map((payment, index) => (
                    <tr
                      key={payment.id || index}
                      style={styles.row}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(to right, #F0F5FF, #F5EDFF, #FFF0F5)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = index % 2 === 0 ? '#FFFFFF' : 'linear-gradient(to right, #FAFCFF, #FBF6FF, #FFF8FB)';
                      }}
                    >
                      <td style={styles.td}>{indexOfFirstPayment + index + 1}</td>
                      <td style={styles.tdStudent}>
                        <div>
                          <div style={{ fontWeight: '400', fontSize: '12px', fontFamily: "'Roboto', sans-serif" }}>
                            {payment.studentName || 'N/A'}
                          </div>
                          <div style={{ fontSize: '10px', color: '#666', fontFamily: "'Roboto', sans-serif" }}>
                            {payment.studentEmail || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td style={styles.td}>{payment.departmentName || 'N/A'}</td>
                      <td style={styles.td}>
                        {payment.date
                          ? new Date(payment.date).toLocaleDateString("en-GB", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })
                          : 'N/A'}
                      </td>
                      <td style={styles.td}>{payment.testName || 'N/A'}</td>
                      <td style={styles.td}>
                        {payment.amount ? formatCurrency(payment.amount, payment.currency) : 'N/A'}
                      </td>
                      <td style={styles.td}>{payment.method || 'N/A'}</td>
                      <td style={styles.tdTransactionId}>
                        <div
                          style={{
                            maxWidth: '120px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontFamily: "'Roboto', sans-serif",
                          }}
                          title={payment.transactionId || ''}
                        >
                          {payment.transactionId || 'N/A'}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.statusBadge,
                            backgroundColor: getStatusColor(payment.status).color,
                          }}
                        >
                          {getStatusColor(payment.status).display}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={styles.paginationContainer}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontFamily: "'Roboto', sans-serif" }}>
                  <span style={{ fontSize: '14px', color: '#1F2937', fontFamily: "'Roboto', sans-serif" }}>
                    Showing {indexOfFirstPayment + 1} to {Math.min(indexOfLastPayment, filteredPayments.length)} of {filteredPayments.length} entries
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontFamily: "'Roboto', sans-serif" }}>
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      ...styles.pageButtonPrevious,
                      backgroundColor: currentPage === 1 ? '#ccc' : undefined,
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    }}
                    className="pagination-button"
                    aria-label="Previous page"
                  >
                    Previous
                  </button>
                  <div style={styles.paginationWrapper}>
                    {Array.from({ length: totalPages }, (_, i) => {
                      const pageNum = i + 1;
                      const shouldShow =
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= currentPage - 2 && pageNum <= currentPage + 2);

                      if (!shouldShow) {
                        if (
                          (pageNum === currentPage - 3 && currentPage > 4) ||
                          (pageNum === currentPage + 3 && currentPage < totalPages - 3)
                        ) {
                          return (
                            <span key={`ellipsis-${pageNum}`} style={{ padding: '0 5px', color: '#6B21A8', fontFamily: "'Roboto', sans-serif" }}>
                              ...
                            </span>
                          );
                        }
                        return null;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => paginate(pageNum)}
                          style={{
                            ...styles.pageNumberButton,
                            background: currentPage === pageNum ? 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)' : '#ffffff',
                            color: currentPage === pageNum ? '#6B21A8' : '#333',
                          }}
                          className={`pagination-button page-number-button ${currentPage === pageNum ? 'active' : ''}`}
                          aria-label={`Page ${pageNum}`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{
                      ...styles.pageButton,
                      backgroundColor: currentPage === totalPages ? '#ccc' : undefined,
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    }}
                    className="pagination-button"
                    aria-label="Next page"
                  >
                    Next
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontFamily: "'Roboto', sans-serif", position: 'relative' }} data-page-dropdown>
                  <div
                    className="custom-dropdown"
                    style={styles.customDropdown}
                    onClick={() => setIsPageDropdownOpen(!isPageDropdownOpen)}
                  >
                    {currentPage}
                    {isPageDropdownOpen && (
                      <div className="custom-dropdown-options" style={styles.customDropdownOptions}>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                          <div
                            key={pageNum}
                            className="custom-option"
                            style={styles.customOption}
                            onClick={() => {
                              paginate(pageNum);
                              setIsPageDropdownOpen(false);
                            }}
                          >
                            {pageNum}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AllPayments;